import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { LayoutGrid, BarChart3, History, Settings, Receipt, Coffee, PackageOpen, LogOut, HandCoins } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const navItems = [
  { to: "/", label: "Tables", icon: LayoutGrid, roles: ["Admin", "Manager"] },
  { to: "/dashboard", label: "Dashboard", icon: BarChart3, roles: ["Admin"] },
  { to: "/cafe", label: "Cafe", icon: Coffee, roles: ["Admin", "Manager"] },
  { to: "/stock", label: "Stock", icon: PackageOpen, roles: ["Admin"] },
  { to: "/expenses", label: "Expenses", icon: Receipt, roles: ["Admin"] },
  { to: "/history", label: "History", icon: History, roles: ["Admin", "Manager"] },
  { to: "/dues", label: "Dues", icon: HandCoins, roles: ["Admin", "Manager"] },
  { to: "/settings", label: "Settings", icon: Settings, roles: ["Admin"] },
];

const logoSrc = `${process.env.PUBLIC_URL}/assets/chakracue-logo.jpeg`;

export default function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const filteredNavItems = navItems.filter((item) => item.roles.includes(user?.role));

  const handleSignOut = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#09090b] flex flex-col md:flex-row">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex flex-col w-64 bg-[#111113] border-r border-zinc-800 fixed h-screen z-30">
        <div className="p-6 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <img
              src={logoSrc}
              alt="ChakraCue logo"
              className="h-12 w-12 shrink-0 rounded-lg border border-amber-500/30 object-cover shadow-lg shadow-black/30"
            />
            <div className="min-w-0">
              <h1 className="font-heading text-2xl font-bold text-zinc-100 tracking-tight leading-none">
                ChakraCue
              </h1>
              <p className="text-zinc-500 text-xs mt-1 tracking-wide uppercase">Enter the Circle</p>
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between gap-3 rounded-2xl border border-zinc-700 bg-zinc-950/60 p-3">
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Signed in as</p>
              <p className="text-sm font-medium text-zinc-100">{user?.role || "Guest"}</p>
            </div>
            <button
              type="button"
              onClick={handleSignOut}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-zinc-700 text-zinc-400 transition hover:bg-zinc-900 hover:text-white"
              aria-label="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {filteredNavItems.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`nav-${label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? "bg-green-600/10 text-green-400 border border-green-500/20"
                    : "text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/50"
                }`
              }
            >
              <Icon className="w-5 h-5" />
              {label}
            </NavLink>
          ))}
        </nav>
      </aside>

      {/* Mobile header — the sidebar is hidden on phones, and sign-out lived
          only there, so the caretaker had no way to log out. */}
      <header className="md:hidden sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-zinc-800 bg-[#111113] px-4 py-3">
        <div className="flex items-center gap-2 min-w-0">
          <img
            src={logoSrc}
            alt="ChakraCue logo"
            className="h-8 w-8 shrink-0 rounded-lg border border-amber-500/30 object-cover"
          />
          <div className="min-w-0">
            <p className="font-heading text-lg font-bold leading-none text-zinc-100 truncate">ChakraCue</p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{user?.role || "Guest"}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleSignOut}
          data-testid="mobile-sign-out"
          className="inline-flex items-center gap-2 rounded-full border border-zinc-700 px-3 py-1.5 text-xs font-medium text-zinc-300 transition hover:bg-zinc-800 hover:text-white shrink-0"
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </button>
      </header>

      {/* Main Content */}
      <main className="flex-1 md:ml-64 pb-20 md:pb-0 min-h-screen">
        <div className="p-4 md:p-6 lg:p-8">{children}</div>
      </main>

      {/* Mobile Bottom Nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-[#111113] border-t border-zinc-800 z-30 flex safe-area-bottom">
        {filteredNavItems.map(({ to, label, icon: Icon }) => {
          const isActive = location.pathname === to || (to !== "/" && location.pathname.startsWith(to));
          const isHome = to === "/" && location.pathname === "/";
          const active = isActive || isHome;
          return (
            <NavLink
              key={to}
              to={to}
              data-testid={`mobile-nav-${label.toLowerCase()}`}
              className={`flex-1 flex flex-col items-center py-2.5 gap-0.5 transition-colors duration-200 ${
                active ? "text-green-400" : "text-zinc-500"
              }`}
            >
              <Icon className="w-[18px] h-[18px]" />
              <span className="text-[9px] font-medium leading-tight">{label}</span>
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}
